import * as _ from 'lodash';

import {
  ACTIVATE_FILTER,
  ACTIVATE_FILTERS,
  CHECK_ALL_EXPENSES,
  CHECK_EXPENSE,
  CLOSE_DATE_RANGE_FILTER,
  CLOSE_MENU,
  DEACTIVATE_ALL_FILTERS,
  DEACTIVATE_AMOUNT_FILTERS,
  DEACTIVATE_FILTER,
  DECREMENT_REQUEST_COUNT,
  INCREMENT_REQUEST_COUNT,
  OPEN_DATE_RANGE_FILTER,
  OPEN_MENU,
  RECEIVE_SEARCH_RESULTS,
  SELECT_ALL_EXPENSES,
  SELECT_EXPENSE,
  SELECT_SAVED_SEARCH,
  SET_SEARCH_VIEW,
  SET_VISIBLE_EXPENSES,
  SearchAction,
  UNCHECK_ALL_EXPENSES,
  UNCHECK_EXPENSE,
  UNSELECT_ALL_EXPENSES,
  UNSELECT_EXPENSE
} from '@Actions/search';

import { IFilterObject } from '@ClientModels/filters';
import { ISavedSearchModel } from '@Shared/models';

import { DEFAULT_ACTIVE_FILTERS, DEFAULT_FILTERS } from './filters';

interface ISearchState {
  selectedExpenseId: string;
  selectedSavedSearch: ISavedSearchModel | null;
  filters: IFilterObject;
  activeFilters: IFilterObject;
  checkedExpenseIds: string[];
  dateRangeModalOpen: boolean;
  openMenu: boolean;
  requestCount: number;
  count: number;
  errored: boolean;
  visibleExpenseIds: string[];
  view: string;
}

export const defaultState: ISearchState = {
  selectedExpenseId: null,
  selectedSavedSearch: null,
  filters: _.merge({}, DEFAULT_FILTERS) as IFilterObject,
  activeFilters: _.merge({}, DEFAULT_ACTIVE_FILTERS) as IFilterObject,
  checkedExpenseIds: [],
  dateRangeModalOpen: false,
  openMenu: false,
  requestCount: 0,
  count: null,
  errored: false,
  visibleExpenseIds: [],
  view: null
};

// eslint-disable-next-line no-restricted-syntax
export default function (state: ISearchState = defaultState, action: SearchAction) {
  switch (action.type) {
    case SET_VISIBLE_EXPENSES:
      return _.assign({}, state, {
        visibleExpenseIds: action.expenseIds
      });
    case RECEIVE_SEARCH_RESULTS: {
      const visibleExpenseIds = _.map(action.results.rows, 'id');
      return _.assign({}, state, {
        visibleExpenseIds: visibleExpenseIds,
        count: action.results.count,
        checkedExpenseIds: _.filter(state.checkedExpenseIds, (expenseId) => _.includes(visibleExpenseIds, expenseId))
      });
    }
    case SELECT_EXPENSE:
      return _.assign({}, state, {
        selectedExpenseId: action.expenseId
      });
    case UNSELECT_EXPENSE:
      return _.assign({}, state, {
        selectedExpenseId: undefined
      });
    case CHECK_EXPENSE:
      return _.assign({}, state, {
        checkedExpenseIds: _.uniq([...(state.checkedExpenseIds || []), action.expenseId])
      });
    case UNCHECK_EXPENSE:
      return _.assign({}, state, {
        checkedExpenseIds: _.without(state.checkedExpenseIds, action.expenseId)
      });
    case CHECK_ALL_EXPENSES:
      return _.assign({}, state, {
        checkedExpenseIds: action.expenseIds
      });
    case UNCHECK_ALL_EXPENSES:
      return _.assign({}, state, {
        checkedExpenseIds: []
      });
    case SELECT_ALL_EXPENSES:
      return _.assign({}, state, {
        allExpensesSelected: true
      });
    case UNSELECT_ALL_EXPENSES:
      return _.assign({}, state, {
        allExpensesSelected: false
      });
    case ACTIVATE_FILTER: {
      // sets filter to whatever value is passed in. should be called set_filter.
      const filtersByType = _.assign({}, state.activeFilters[action.filterType], {
        [action.filterName]: action.value
      });
      const activeFilters = _.assign({}, state.activeFilters, {
        [action.filterType]: filtersByType
      });

      return _.assign({}, state, { activeFilters });
    }

    case ACTIVATE_FILTERS: {
      // sets filter to whatever value is passed in. should be called set_filter.
      return _.assign({}, state, {
        activeFilters: action.filters
      });
    }

    case DEACTIVATE_AMOUNT_FILTERS: {
      const newGeneralFilters = _.omit(state.activeFilters.general, ['amount', 'amount_above', 'amount_below']);
      const newActiveFilters = { general: newGeneralFilters, date: state.activeFilters.date }; // jting: this might not be kosher
      return _.assign({}, state, {
        activeFilters: newActiveFilters
      });
    }

    case DEACTIVATE_FILTER: {
      // removes whatever value is passed in to list
      let updatedFilterValues = [];
      const valuesToRemove = action.value.map(({ value }) => value);

      if (state.activeFilters && state.activeFilters[action.filterType][action.filterName] && valuesToRemove.length > 0) {
        updatedFilterValues = _.filter(state.activeFilters[action.filterType][action.filterName], function (item) {
          return !valuesToRemove.includes(item.value);
        });
      }
      const nextStateForType = _.assign({}, state.activeFilters[action.filterType], { [action.filterName]: updatedFilterValues });
      const nextActiveFilters = _.assign({}, state.activeFilters, { [action.filterType]: nextStateForType });

      return _.assign({}, state, {
        activeFilters: nextActiveFilters
      });
    }
    case DEACTIVATE_ALL_FILTERS: {
      return _.assign({}, state, {
        activeFilters: DEFAULT_ACTIVE_FILTERS
      });
    }

    case OPEN_MENU: {
      return _.assign({}, state, {
        openMenu: true
      });
    }

    case CLOSE_MENU: {
      return _.assign({}, state, {
        openMenu: false
      });
    }

    case OPEN_DATE_RANGE_FILTER: {
      return _.assign({}, state, {
        dateRangeModalOpen: true
      });
    }

    case CLOSE_DATE_RANGE_FILTER: {
      return _.assign({}, state, {
        dateRangeModalOpen: false
      });
    }

    case SET_SEARCH_VIEW: {
      return _.assign({}, state, {
        view: action.view
      });
    }

    case INCREMENT_REQUEST_COUNT: {
      return _.assign({}, state, {
        requestCount: state.requestCount + 1
      });
    }

    case DECREMENT_REQUEST_COUNT: {
      return _.assign({}, state, {
        requestCount: state.requestCount - 1
      });
    }

    case SELECT_SAVED_SEARCH: {
      return { ...state, selectedSavedSearch: action.savedSearch };
    }

    default:
      return state;
  }
}
